import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User,
  sendEmailVerification,
  updateProfile,
  reload
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import { auth, db } from '../config/firebase';

export interface UserData {
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  countryCode: string;
  emailVerified: boolean;
  createdAt: any;
  updatedAt: any;
}

export interface AuthResponse {
  success: boolean;
  user?: User;
  userData?: UserData;
  error?: string;
}

/**
 * Register a new user with email and password
 */
export const registerUser = async (
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  phone: string,
  countryCode: string = '+91'
): Promise<AuthResponse> => {
  try {
    // Create user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Update user profile with display name
    await updateProfile(user, {
      displayName: `${firstName} ${lastName}`
    });

    // Send email verification with custom continue URL
    const actionCodeSettings = {
      url: `${window.location.origin}/verify-email?continueUrl=${encodeURIComponent('/donate-anonymously')}`,
      handleCodeInApp: true,
    };
    await sendEmailVerification(user, actionCodeSettings);

    // Create user document in Firestore
    const userData: UserData = {
      uid: user.uid,
      email: user.email || email,
      firstName,
      lastName,
      phone,
      countryCode,
      emailVerified: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, 'users', user.uid), userData);

    return {
      success: true,
      user,
      userData
    };
  } catch (error: any) {
    console.error('Registration error:', error);
    return {
      success: false,
      error: getAuthErrorMessage(error.code)
    };
  }
};

/**
 * Login existing user with email and password
 */
export const loginUser = async (
  email: string,
  password: string
): Promise<AuthResponse> => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Reload user to ensure we have the latest emailVerified status
    await reload(user);

    // Fetch user data from Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    
    let userData: UserData | undefined;
    if (userDoc.exists()) {
      userData = userDoc.data() as UserData;
      // Update emailVerified status in Firestore if it changed
      if (userData.emailVerified !== user.emailVerified) {
        await updateDoc(doc(db, 'users', user.uid), {
          emailVerified: user.emailVerified,
          updatedAt: serverTimestamp()
        });
        userData.emailVerified = user.emailVerified;
      }
    } else {
      // If user document doesn't exist, create it from auth data
      userData = {
        uid: user.uid,
        email: user.email || email,
        firstName: user.displayName?.split(' ')[0] || '',
        lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
        phone: '',
        countryCode: '+91',
        emailVerified: user.emailVerified,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      await setDoc(doc(db, 'users', user.uid), userData);
    }

    return {
      success: true,
      user,
      userData
    };
  } catch (error: any) {
    console.error('Login error:', error);
    return {
      success: false,
      error: getAuthErrorMessage(error.code)
    };
  }
};

/**
 * Check if user exists by email
 */
export const checkUserExists = async (email: string): Promise<boolean> => {
  try {
    // Check in Firestore users collection
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('email', '==', email.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking user existence:', error);
    return false;
  }
};

/**
 * Get current authenticated user
 */
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};

/**
 * Get current user data from Firestore
 */
export const getCurrentUserData = async (): Promise<UserData | null> => {
  const user = auth.currentUser;
  if (!user) return null;

  try {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (userDoc.exists()) {
      return userDoc.data() as UserData;
    }
    return null;
  } catch (error) {
    console.error('Error fetching user data:', error);
    return null;
  }
};

/**
 * Logout current user
 */
export const logoutUser = async (): Promise<void> => {
  await signOut(auth);
};

/**
 * Subscribe to auth state changes
 */
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Reload current user to get updated emailVerified status
 */
export const reloadCurrentUser = async (): Promise<User | null> => {
  const user = auth.currentUser;
  if (user) {
    await reload(user);
    // Update Firestore document with latest emailVerified status
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        emailVerified: user.emailVerified,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating Firestore emailVerified status:', error);
    }
    return user;
  }
  return null;
};

/**
 * Resend email verification
 */
export const resendEmailVerification = async (): Promise<{ success: boolean; error?: string }> => {
  const user = auth.currentUser;
  if (!user) {
    return { success: false, error: 'You must be logged in to resend verification email.' };
  }

  if (user.emailVerified) {
    return { success: false, error: 'Your email is already verified.' };
  }

  try {
    const actionCodeSettings = {
      url: `${window.location.origin}/verify-email?continueUrl=${encodeURIComponent('/donate-anonymously')}`,
      handleCodeInApp: true,
    };
    await sendEmailVerification(user, actionCodeSettings);
    return { success: true };
  } catch (error: any) {
    console.error('Error resending verification email:', error);
    return {
      success: false,
      error: getAuthErrorMessage(error.code) || 'Failed to send verification email. Please try again.'
    };
  }
};

/**
 * Get user-friendly error messages
 */
const getAuthErrorMessage = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return 'This email is already registered. Please login instead.';
    case 'auth/invalid-email':
      return 'Invalid email address.';
    case 'auth/operation-not-allowed':
      return 'Email/password accounts are not enabled.';
    case 'auth/weak-password':
      return 'Password should be at least 6 characters.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/user-not-found':
      return 'No account found with this email. Please register first.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/invalid-credential':
      return 'The email address or password you entered is incorrect. Please check your credentials and try again.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.';
    default:
      return 'An error occurred. Please try again.';
  }
};






